'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import Image from 'next/image';
import geneNotFoundIcon from '@images/gene-404.webp';
import { TrackedAnchor } from '@/components/ancher';
import { Locale, useLocale } from 'next-intl';

const getGeneNotFoundContent = (locale: Locale, geneId: string) => {
  const content = {
    ko: {
      title: `${geneId} 정보 준비 중`,
      message1: '이 유전자의 상세 정보는 빠른 시일 내 제공될 예정입니다.',
      message2: '업데이트 알림을 받아보세요.',
      browseOther: '다른 유전자 둘러보기',
      getNotification: '알림 받기',
      getNotificationLink: 'https://go.3billion.io/l/1084872/2026-01-13/blc4th',
    },
    en: {
      title: `${geneId} information coming soon`,
      message1: 'Detailed information for this gene will be available soon.',
      message2: "Subscribe to get notified when it's ready.",
      browseOther: 'Browse other genes',
      getNotification: 'Get updates',
      getNotificationLink: 'https://go.3billion.io/l/1084872/2026-01-13/blc4td',
    },
  } as const;

  return content[locale];
};

export default function NotFound() {
  const pathname = usePathname();
  const locale = useLocale();

  const geneId = pathname.split('/').pop() ?? '';
  const content = getGeneNotFoundContent(locale, geneId);

  // 준비중 페이지 진입 시 이전 페이지(커버리지 브라우저) 스크롤 위치가 남아 중앙이 안 맞아 보이므로 최상단으로 리셋한다.
  // 전역 scroll-behavior: smooth 를 상속해 애니메이션되지 않도록 behavior: 'instant' 로 즉시 이동한다.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);

  return (
    <div className="mt-[80px] flex flex-col items-center py-[80px] 2xl:mt-[100px]">
      <div className="flex flex-col items-center gap-6 md:py-[80px]">
        <Image
          src={geneNotFoundIcon}
          alt="Gene Not Found Icon"
          width={72}
          height={75}
          className="h-[75px] w-[72px]"
        />

        <div className="flex flex-col items-center gap-3 text-center">
          <h1 className="text-h6 font-semibold text-3b-text-primary md:text-h4">
            {content.title}
          </h1>
          <p className="text-body2 font-medium text-3b-text-secondary md:text-sh2">
            {content.message1}
            <br />
            {content.message2}
          </p>
        </div>

        <div className="flex flex-col items-center gap-3 md:flex-row">
          <TrackedAnchor
            href="/gene-browser"
            label="browse-other-genes"
            position="main_content"
            className="flex h-10 items-center justify-center rounded-[30px] bg-3b-background-medium px-5 md:h-12 md:rounded-[40px] md:px-7"
          >
            <span className="text-body2 font-semibold text-3b-text-primary md:text-sh2">
              {content.browseOther}
            </span>
          </TrackedAnchor>
          <TrackedAnchor
            href={content.getNotificationLink}
            label="get-notification"
            position="main_content"
            className="flex h-10 w-full items-center justify-center rounded-[30px] bg-3b-blue-light-900 px-5 md:h-12 md:w-auto md:rounded-[40px] md:px-7"
          >
            <span className="text-body2 font-semibold text-3b-text-contrast md:text-sh2">
              {content.getNotification}
            </span>
          </TrackedAnchor>
        </div>
      </div>
    </div>
  );
}
